import * as React from "react";
import { useLocation } from "react-router-dom";

// Each value is the trailing URL path segment of a child route registered
// under the `:datasetPath` parent in DatasetsRoutes. The string values are
// load-bearing: they are matched against `location.pathname` to identify
// the active tab, and used as `<Link to={...}>` targets to navigate between
// tabs. Renaming a value requires updating the matching `<Route path>` in
// DatasetsRoutes.
export enum DatasetTab {
  Files = "files",
  Sessions = "sessions",
  Topics = "topics",
  Events = "events",
  Actions = "actions",
  AgentThreads = "agent-threads",
  Details = "details",
  Permissions = "permissions",
  AISummary = "summary",
}

function deriveActiveTabFromUrl(path: string | undefined) {
  switch (path) {
    case DatasetTab.Sessions:
      return DatasetTab.Sessions;
    case DatasetTab.Topics:
      return DatasetTab.Topics;
    case DatasetTab.Events:
      return DatasetTab.Events;
    case DatasetTab.Actions:
      return DatasetTab.Actions;
    case DatasetTab.AgentThreads:
      return DatasetTab.AgentThreads;
    case DatasetTab.Details:
      return DatasetTab.Details;
    case DatasetTab.Permissions:
      return DatasetTab.Permissions;
    case DatasetTab.AISummary:
      return DatasetTab.AISummary;
    default:
      return DatasetTab.Files;
  }
}

/**
 * Returns the active dataset tab as a `[tab, setTab]` tuple, derived from
 * the trailing segment of the current URL pathname and re-derived on every
 * pathname change.
 *
 * Any segment that does not match a `DatasetTab` value — the bare
 * `:datasetPath`, deep paths under `/files/...`, or unknown suffixes —
 * resolves to `DatasetTab.Files`, mirroring the route tree's index and
 * wildcard redirects.
 *
 * The setter lets callers driving navigation update the highlighted tab
 * synchronously rather than waiting for the next pathname effect. The
 * pathname remains the source of truth and overwrites any value that
 * diverges from it.
 */
export function useActiveDatasetTab() {
  const location = useLocation();
  const path = location.pathname.split("/").pop();

  const [activeTab, setActiveTab] = React.useState(
    deriveActiveTabFromUrl(path),
  );

  React.useEffect(() => {
    setActiveTab(deriveActiveTabFromUrl(path));
  }, [path]);

  return [activeTab, setActiveTab] as const;
}
